import { usePipelineTemplate } from 'pipelines/hooks/usePipelineTemplate';
import { usePipelineTemplates } from 'pipelines/hooks/usePipelineTemplates';

import { Skeleton } from 'components/Skeleton/Skeleton';

import { MarkdownEmbedProps } from 'services/markdown';

import { PipelineTemplateSmallCard } from './PipelineTemplateSmallCard';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUUID(value: string): boolean {
  return UUID_REGEX.test(value);
}

function SkeletonCard() {
  return (
    <div className="flex min-h-[80px] items-start gap-3 rounded-lg border border-[hsl(var(--ui-separator))] bg-[hsl(var(--ui-fg))] p-3">
      <Skeleton className="aspect-[1.5] w-[90px] shrink-0 rounded-md" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Skeleton className="h-5 w-3/5" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="mt-1 h-8 w-20" />
      </div>
    </div>
  );
}

function SingleTemplate({ id }: { id: string }) {
  const {
    data: template,
    isLoading,
    error,
  } = usePipelineTemplate({ templateId: id });

  if (isLoading) {
    return <SkeletonCard />;
  }

  if (error || !template) {
    return (
      <div className="flex min-h-[80px] items-center justify-center rounded-lg border border-[hsl(var(--ui-separator))] bg-[hsl(var(--ui-fg))] p-3">
        <p className="m-0 text-sm text-[hsl(var(--ui-text-lowContrast))]">
          Failed to load pipeline template{id ? ` (${id})` : ''}
        </p>
      </div>
    );
  }

  return <PipelineTemplateSmallCard template={template} />;
}

function SearchTemplates({ query }: { query: string }) {
  const { data, isLoading, error } = usePipelineTemplates({
    params: { query, limit: 10 },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {[1, 2, 3].map((i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[80px] items-center justify-center rounded-lg border border-[hsl(var(--ui-separator))] bg-[hsl(var(--ui-fg))] p-3">
        <p className="m-0 text-sm text-[hsl(var(--ui-text-lowContrast))]">
          Failed to search pipeline templates
        </p>
      </div>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <div className="flex min-h-[80px] items-center justify-center rounded-lg border border-[hsl(var(--ui-separator))] bg-[hsl(var(--ui-fg))] p-3">
        <p className="m-0 text-sm text-[hsl(var(--ui-text-lowContrast))]">
          {query ? `"${query}"` : ''}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {data.data.map((template) => (
        <PipelineTemplateSmallCard key={template.id} template={template} />
      ))}
    </div>
  );
}

export function PipelineTemplateSmallCardList({ id }: MarkdownEmbedProps) {
  const isIdMode = isUUID(id);

  return (
    <div className="my-4">
      {isIdMode ? <SingleTemplate id={id} /> : <SearchTemplates query={id} />}
    </div>
  );
}
